import {Icon} from "@/components/icon/Icon.tsx";
import {IncidentType, UptimeCalculationType} from "@/lib/types/statuspages";
import {ScrollableUptimeBar} from "@/pages/statuspages/consumption/components/ScrollableUptimeBar/ScrollableUptimeBar.tsx";
import {BarEventStatus} from "@uptime-com/uptimebar";
import classNames from "classnames";
import {differenceInSeconds} from "date-fns";
import type {ReactElement} from "react";
import {forwardRef, useCallback, useEffect, useMemo, useRef, useState} from "react";
import type {IconType} from "react-icons";
import {FiAlertTriangle, FiArrowRight, FiCheck, FiTool, FiTrendingDown} from "react-icons/fi";
import {
  computeCheckBasedDowntimeInPeriod,
  computeDowntimeInPeriod,
  generateEventsFromDowntimes,
  generateEventsFromIncidents,
} from "../../utils/uptimeBarEvents.ts";
import {formatUptimePercentage} from "../../utils/uptimeFormat.ts";
import {StatusPageUptimeBarTooltip} from "../StatusPageUptimeBarTooltip/StatusPageUptimeBarTooltip.tsx";
import styles from "./StatusPageStatusBar.module.scss";
import type {
  PillToolTipProps,
  StatusPageStatusBarProps,
  StatusPageStatusBarVariant,
} from "./StatusPageStatusBar.types.ts";

interface GlobalStatusConfig {
  icon: IconType;
  title: string;
  variant: StatusPageStatusBarVariant;
  iconClass: string;
}

const getGlobalStatusConfig = (
  globalIsOperational: boolean,
  hasMaintenanceState: boolean,
  worstStatus: string | null
): GlobalStatusConfig => {
  if (globalIsOperational) {
    return {
      icon: FiCheck,
      title: "All systems operational",
      variant: "operational",
      iconClass: styles.iconOperational,
    };
  }
  if (worstStatus === "major-outage") {
    return {
      icon: FiAlertTriangle,
      title: "Experiencing a major service disruption",
      variant: "disruption",
      iconClass: styles.iconDisruption,
    };
  }
  if (worstStatus === "partial-outage") {
    return {
      icon: FiAlertTriangle,
      title: "Experiencing a partial service disruption",
      variant: "partialDisruption",
      iconClass: styles.iconPartialDisruption,
    };
  }
  if (worstStatus === "degraded-performance") {
    return {
      icon: FiTrendingDown,
      title: "Some systems are experiencing issues",
      variant: "degraded",
      iconClass: styles.iconDegraded,
    };
  }
  if (hasMaintenanceState && !worstStatus) {
    return {
      icon: FiTool,
      title: "Scheduled maintenance in progress",
      variant: "maintenance",
      iconClass: styles.iconMaintenance,
    };
  }
  return {
    icon: FiTrendingDown,
    title: "Some systems are experiencing issues",
    variant: "degraded",
    iconClass: styles.iconDegraded,
  };
};

const formatTimezoneLabel = (timezone: string): string => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "longOffset",
    });
    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find((p) => p.type === "timeZoneName");
    const offset = offsetPart?.value ?? "";
    return `Timezone: (${offset}) ${timezone}`;
  } catch {
    return `Timezone: ${timezone}`;
  }
};

export const StatusPageStatusBar = forwardRef<HTMLDivElement, StatusPageStatusBarProps>(
  (
    {
      incidents,
      downtimeHistory,
      statusMetadata,
      uptimeCalculationType,
      defaultStatusDateRange,
      timezone,
      timeFormat,
      showHistorySnake,
      globalIsOperational,
      hasMaintenanceState,
      components,
      start,
      end,
      onViewIncidents,
      barOnly = false,
      showOverallStatus = true,
      enableMobileScroll = false,
      className,
      ...rest
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [theme, setTheme] = useState<string | null>(null);

    useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      const themeRoot = el.closest("[data-theme]");
      if (!themeRoot) return;

      setTheme(themeRoot.getAttribute("data-theme"));
      const observer = new MutationObserver(() => {
        setTheme(themeRoot.getAttribute("data-theme"));
      });
      observer.observe(themeRoot, {attributes: true, attributeFilter: ["data-theme"]});
      return () => observer.disconnect();
    }, []);

    const worstStatus = useMemo(() => {
      const rankMap: Record<string, number> = {
        "major-outage": 1,
        "partial-outage": 2,
        "degraded-performance": 3,
        notification: 4,
      };

      let worst: string | null = null;
      let worstRank = Infinity;

      // For check-based pages, derive status solely from downtime_history.
      // Component statuses may be tainted by incident state on BY_CHECKS pages.
      if (uptimeCalculationType === UptimeCalculationType.BY_CHECKS) {
        const hasActiveDowntime = downtimeHistory.some((d) => d.end_ts === null);
        return hasActiveDowntime ? "major-outage" : null;
      }

      const allComponents = components.flatMap((c) => (c.subcomponents ? [c, ...c.subcomponents] : [c]));
      for (const c of allComponents) {
        if (c.status === "operational" || c.status === "under-maintenance") continue;
        const rank = rankMap[c.status] ?? 99;
        if (rank < worstRank) {
          worstRank = rank;
          worst = c.status;
        }
      }

      for (const inc of incidents) {
        if (!inc.ends_at) {
          for (const afc of inc.affected_components) {
            const rank = rankMap[afc.status] ?? 99;
            if (rank < worstRank) {
              worstRank = rank;
              worst = afc.status;
            }
          }
        }
      }

      return worst;
    }, [components, incidents, uptimeCalculationType, downtimeHistory]);

    const isCheckBased = uptimeCalculationType === UptimeCalculationType.BY_CHECKS;

    // `end` deliberately reaches end-of-day: legacy's snake also ends at endOfDay + 1s and fills
    // gaps with SUCCESS (inspire/snake/InspireDateRangeSnake.tsx:38). Do not clamp to `now` — the
    // drilldown does, but only because it mirrors legacy's check-analysis statechart.
    const events = useMemo(() => {
      if (isCheckBased) {
        return generateEventsFromDowntimes(downtimeHistory, start, end);
      }
      return generateEventsFromIncidents(incidents, statusMetadata, start, end);
    }, [isCheckBased, incidents, downtimeHistory, statusMetadata, start, end]);

    // Unlike the bar above, this stops at `now` — unelapsed time cannot count as uptime, so it stays
    // out of the denominator. Also the last uptime figure still computed in the browser; the History
    // tab's now come from the backend's global metrics.
    const uptimePercent = useMemo(() => {
      const now = new Date();
      const effectiveEnd = end < now ? end : now;
      const totalSecs = differenceInSeconds(effectiveEnd, start);
      if (totalSecs <= 0) return 100;

      const downtimeSecs = isCheckBased
        ? computeCheckBasedDowntimeInPeriod(downtimeHistory, start, effectiveEnd)
        : computeDowntimeInPeriod(incidents, start, effectiveEnd);

      const pct = ((totalSecs - downtimeSecs) / totalSecs) * 100;
      return Math.min(100, Math.max(0, Number(pct.toFixed(3))));
    }, [isCheckBased, incidents, downtimeHistory, start, end]);

    const pillTooltipRenderer = useCallback(
      ({periodStart, periodEnd}: PillToolTipProps): ReactElement => (
        <StatusPageUptimeBarTooltip
          periodStart={periodStart}
          periodEnd={periodEnd}
          incidents={incidents}
          statusMetadata={statusMetadata}
          downtimeHistory={downtimeHistory}
          uptimeCalculationType={uptimeCalculationType}
          timezone={timezone}
          timeFormat={timeFormat}
        />
      ),
      [incidents, statusMetadata, downtimeHistory, uptimeCalculationType, timezone, timeFormat]
    );

    const activeManualIncidents = useMemo(() => {
      if (!isCheckBased) return [];
      return incidents.filter((inc) => inc.incident_type === IncidentType.INCIDENT && !inc.ends_at);
    }, [isCheckBased, incidents]);

    const incidentBannerVariant = useMemo((): "disruption" | "partialDisruption" | "degraded" | null => {
      if (activeManualIncidents.length === 0) return null;
      const rankMap: Record<string, number> = {
        "major-outage": 1,
        "partial-outage": 2,
        "degraded-performance": 3,
      };
      let worstRank = Infinity;
      for (const inc of activeManualIncidents) {
        if (inc.affected_components.length === 0) {
          return "disruption";
        }
        for (const afc of inc.affected_components) {
          const rank = rankMap[afc.status] ?? 99;
          if (rank < worstRank) {
            worstRank = rank;
          }
        }
      }
      if (worstRank === 1) return "disruption";
      if (worstRank === 2) return "partialDisruption";
      return "degraded";
    }, [activeManualIncidents]);

    if (!showHistorySnake) return null;

    const setRef = (node: HTMLDivElement | null) => {
      containerRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    };

    const uptimeBar = (
      <ScrollableUptimeBar
        start={start}
        end={end}
        timezone={timezone}
        events={events}
        size="large"
        defaultMissingEventStatus={BarEventStatus.OPERATIONAL}
        toolTipContentProps={{className: styles.tooltipContent, ...(theme ? {"data-theme": theme} : {})}}
        segmentGapHeight={0}
        pillToolTip={pillTooltipRenderer}
        hoverPill
        stripedNoData
        enableMobileScroll={enableMobileScroll}
      />
    );

    if (barOnly) {
      return (
        <div ref={setRef} className={classNames(styles.barOnly, className)} {...rest}>
          <div className={styles.barContainer}>{uptimeBar}</div>
        </div>
      );
    }

    // For check-based pages, determine operational state solely from worstStatus
    // (derived from active downtimes). The backend's globalIsOperational may
    // reflect incident state which should not color the status bar background.
    const isOperational = isCheckBased ? !worstStatus : globalIsOperational && !worstStatus;
    const statusConfig = getGlobalStatusConfig(isOperational, hasMaintenanceState, worstStatus);
    const dateRangeLabel = `Last ${defaultStatusDateRange} Days`;
    const timezoneLabel = formatTimezoneLabel(timezone);

    return (
      <div
        ref={setRef}
        className={classNames(styles.statusBar, showOverallStatus && styles[statusConfig.variant], className)}
        {...rest}
      >
        {showOverallStatus && (
          <div className={styles.statusTextArea}>
            <Icon icon={statusConfig.icon} size="normal" className={statusConfig.iconClass} />
            <span className={styles.statusTitle}>{statusConfig.title}</span>
            <span className={styles.timezoneText}>{timezoneLabel}</span>
          </div>
        )}

        <div className={styles.row}>
          <span className={styles.uptimePercent}>{formatUptimePercentage(uptimePercent)}</span>
          <span className={styles.dateRange}>{dateRangeLabel}</span>
        </div>

        <div className={styles.barContainer}>{uptimeBar}</div>

        {incidentBannerVariant && (
          <div
            className={classNames(styles.incidentBanner, styles[`incidentBanner--${incidentBannerVariant}`])}
            role={onViewIncidents ? "button" : undefined}
            tabIndex={onViewIncidents ? 0 : undefined}
            onClick={onViewIncidents}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && onViewIncidents) {
                e.preventDefault();
                onViewIncidents();
              }
            }}
          >
            <div className={styles.incidentBannerContent}>
              <Icon icon={FiAlertTriangle} size="normal" className={styles.incidentBannerIcon} />
              <span className={styles.incidentBannerText}>
                {activeManualIncidents.length} Active Incident{activeManualIncidents.length !== 1 ? "s" : ""}
              </span>
            </div>
            {onViewIncidents && (
              <div className={styles.incidentBannerLink}>
                <span>View all</span>
                <Icon icon={FiArrowRight} size="normal" />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

StatusPageStatusBar.displayName = "StatusPageStatusBar";
